// B1 content-first primitive generation (MOBILE_CONTENT_FIRST_PLAN_2026-07-06
// 阶段 B / B1). These primitives converge the six earlier generations (p0c /
// p1SecondAudit / p1v4 / p2v2 / p2SecondAudit / p3v1) into one content-first
// vocabulary. Design laws they encode:
// - 法则一: a card carries content or an action, never narration. The only
//   narration slot is the single `explainer` line on P5ContentHero.
// - 法则二: the same information unit appears at most once per page, so the
//   hero `title` is optional — pages that keep the TitleBar heading omit it.
// Business UI must import these through mobilePrimitives.tsx (Mobile* aliases)
// only. Old primitive generations stay untouched until B2/B3 page migrations
// complete; the retirement plan lives in .logs/b1_primitive_migration_map.md.
import type { DetailsHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";

export type P5ContentTone = "positive" | "warning" | "info" | "neutral";

type P5ContentHeroProps = Omit<HTMLAttributes<HTMLElement>, "title"> & {
	action?: ReactNode;
	children: ReactNode;
	explainer?: ReactNode;
	eyebrow?: ReactNode;
	media?: ReactNode;
	title?: ReactNode;
};

// Content opening block: the first screen IS the content (score / leaderboard
// / roster rows go in children). No description paragraph slot exists — the
// one-line `explainer` is the only guidance text allowed (single-explainer-line
// contract evidence), and the V1 audit can count it via data-p5-explainer-lines.
export const P5ContentHero = ({
	action,
	children,
	className,
	explainer,
	eyebrow,
	media,
	title,
	...sectionProps
}: P5ContentHeroProps) => (
	<section
		className={clsx("mobile-content-hero", "p5-content-hero", className)}
		data-mobile-content-hero=""
		data-p5-content-hero=""
		data-p5-content-first-screen=""
		data-p5-explainer-lines={explainer ? "1" : "0"}
		{...sectionProps}
	>
		{eyebrow || title || explainer || media ? (
			<header className="mobile-content-hero-header p5-content-hero-header">
				{media ? (
					<div
						className="mobile-content-hero-media p5-content-hero-media"
						data-p5-content-hero-media=""
					>
						{media}
					</div>
				) : null}
				<div className="mobile-content-hero-heading p5-content-hero-heading">
					{eyebrow ? (
						<div className="mobile-content-hero-eyebrow p5-content-hero-eyebrow">{eyebrow}</div>
					) : null}
					{title ? (
						<h2 className="mobile-content-hero-title p5-content-hero-title">{title}</h2>
					) : null}
					{explainer ? (
						<p
							className="mobile-content-hero-explainer p5-content-hero-explainer"
							data-p5-single-explainer-line=""
						>
							{explainer}
						</p>
					) : null}
				</div>
			</header>
		) : null}
		<div
			className="mobile-content-hero-content p5-content-hero-content"
			data-p5-content-hero-content=""
		>
			{children}
		</div>
		{action ? (
			<div
				className="mobile-content-hero-action p5-content-hero-action"
				data-p5-content-hero-action=""
			>
				{action}
			</div>
		) : null}
	</section>
);

type P5EntityRowProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
	detail?: ReactNode;
	highlighted?: boolean;
	media?: ReactNode;
	meta?: ReactNode;
	rank?: ReactNode;
	title: ReactNode;
	trailing?: ReactNode;
};

// Entity row with an avatar/logo media slot. C2 fills the slot with face
// thumbnails / team logos; until then the slot is marked "reserved" so audits
// can distinguish "not yet wired" from "intentionally none".
export const P5EntityRow = ({
	className,
	detail,
	highlighted = false,
	media,
	meta,
	rank,
	title,
	trailing,
	...divProps
}: P5EntityRowProps) => (
	<div
		className={clsx("mobile-entity-row", "p5-entity-row", className, {
			"mobile-entity-row-highlighted": highlighted,
			"p5-entity-row-highlighted": highlighted,
		})}
		data-mobile-entity-row=""
		data-p5-entity-row=""
		data-p5-entity-highlight={highlighted ? "true" : "false"}
		data-p5-entity-media={media ? "filled" : "reserved"}
		{...divProps}
	>
		{rank !== undefined && rank !== null ? (
			<div className="mobile-entity-row-rank p5-entity-row-rank">{rank}</div>
		) : null}
		{media ? (
			<div className="mobile-entity-row-media p5-entity-row-media" data-p5-media-slot="">
				{media}
			</div>
		) : null}
		<div className="mobile-entity-row-main p5-entity-row-main">
			<div className="mobile-entity-row-title p5-entity-row-title">{title}</div>
			{meta ? <div className="mobile-entity-row-meta p5-entity-row-meta">{meta}</div> : null}
			{detail ? (
				<div className="mobile-entity-row-detail p5-entity-row-detail">{detail}</div>
			) : null}
		</div>
		{trailing ? (
			<div className="mobile-entity-row-trailing p5-entity-row-trailing">{trailing}</div>
		) : null}
	</div>
);

type P5StatRowProps = HTMLAttributes<HTMLDivElement> & {
	detail?: ReactNode;
	highlighted?: boolean;
	label: ReactNode;
	rank?: ReactNode;
	tone?: P5ContentTone;
	value: ReactNode;
};

// Numeric row: one metric label, one value, optional league rank. Pure data —
// narration belongs in the hero explainer, if anywhere.
export const P5StatRow = ({
	className,
	detail,
	highlighted = false,
	label,
	rank,
	tone = "neutral",
	value,
	...divProps
}: P5StatRowProps) => (
	<div
		className={clsx("mobile-stat-row", "p5-stat-row", className, {
			"mobile-stat-row-highlighted": highlighted,
			"p5-stat-row-highlighted": highlighted,
		})}
		data-mobile-stat-row=""
		data-p5-stat-row=""
		data-p5-stat-highlight={highlighted ? "true" : "false"}
		data-p5-stat-tone={tone}
		{...divProps}
	>
		<div className="mobile-stat-row-main p5-stat-row-main">
			<div className="mobile-stat-row-label p5-stat-row-label">{label}</div>
			{detail ? <div className="mobile-stat-row-detail p5-stat-row-detail">{detail}</div> : null}
		</div>
		{rank !== undefined && rank !== null ? (
			<div className="mobile-stat-row-rank p5-stat-row-rank">{rank}</div>
		) : null}
		<div className="mobile-stat-row-value p5-stat-row-value">{value}</div>
	</div>
);

export const clampP5RankBarRatio = (ratio: number) =>
	Number.isFinite(ratio) ? Math.min(1, Math.max(0, ratio)) : 0;

type P5RankBarProps = HTMLAttributes<HTMLDivElement> & {
	label?: ReactNode;
	ratio: number;
	tone?: P5ContentTone;
	valueLabel?: ReactNode;
};

// Win-rate / share bar (C2 standings rows, cap-space shares…). `ratio` is a
// 0..1 fraction, clamped so bad inputs can never overflow the track.
export const P5RankBar = ({
	className,
	label,
	ratio,
	tone = "neutral",
	valueLabel,
	...divProps
}: P5RankBarProps) => {
	const clampedRatio = clampP5RankBarRatio(ratio);
	const percentage = Math.round(clampedRatio * 1000) / 10;

	return (
		<div
			className={clsx("mobile-rank-bar", "p5-rank-bar", className)}
			data-mobile-rank-bar=""
			data-p5-rank-bar=""
			data-p5-rank-bar-tone={tone}
			{...divProps}
		>
			{label ? <div className="mobile-rank-bar-label p5-rank-bar-label">{label}</div> : null}
			<div
				aria-valuemax={100}
				aria-valuemin={0}
				aria-valuenow={percentage}
				className="mobile-rank-bar-track p5-rank-bar-track"
				role="progressbar"
			>
				<div
					className="mobile-rank-bar-fill p5-rank-bar-fill"
					data-p5-rank-bar-fill=""
					style={{ width: `${percentage}%` }}
				/>
			</div>
			{valueLabel !== undefined && valueLabel !== null ? (
				<div className="mobile-rank-bar-value p5-rank-bar-value">{valueLabel}</div>
			) : null}
		</div>
	);
};

type P5ExpertDisclosureProps = DetailsHTMLAttributes<HTMLDetailsElement> & {
	children?: ReactNode;
	count?: ReactNode;
	expertHostId: string;
	firstScreenMetricCount?: 2 | 3;
	summary: ReactNode;
};

// Expert-depth fold: the single successor for P2SecondAuditExpertDepth
// (datatable-policy marker + first-screen metric count) and
// the old P3V1 advanced-archive disclosure. Keeps the shared
// data-mobile-advanced-disclosure marker so existing CSS and audit greps keep
// recognizing it during the B2/B3 coexistence window.
export const P5ExpertDisclosure = ({
	children,
	className,
	count,
	expertHostId,
	firstScreenMetricCount,
	summary,
	...detailsProps
}: P5ExpertDisclosureProps) => (
	<details
		className={clsx("mobile-advanced-disclosure", "mobile-expert-disclosure", "p5-expert-disclosure", className)}
		data-mobile-advanced-disclosure=""
		data-mobile-expert-disclosure=""
		data-mobile-expert-disclosure-host={expertHostId}
		data-p5-datatable-policy="expert-only"
		data-p5-expert-disclosure=""
		data-p5-first-screen-metric-count={firstScreenMetricCount}
		{...detailsProps}
	>
		<summary
			className="mobile-expert-disclosure-summary p5-expert-disclosure-summary"
			data-mobile-expert-disclosure-summary=""
		>
			<span className="mobile-expert-disclosure-label p5-expert-disclosure-label">{summary}</span>
			{count !== undefined && count !== null ? (
				<span className="mobile-expert-disclosure-count p5-expert-disclosure-count">{count}</span>
			) : null}
		</summary>
		<div
			className="mobile-expert-disclosure-body p5-expert-disclosure-body"
			data-mobile-expert-disclosure-content=""
		>
			{children}
		</div>
	</details>
);
